import {
  type CartGiftWithPurchaseAttributeValue,
  type CartProductConnection,
} from "../state";
import {
  type ComponentizableCartLine,
  type CartLine,
  type Maybe,
  type AttributeInput,
} from "@shopify/hydrogen-react/storefront-api-types";
import { type PartialDeep } from "type-fest";
import { STOREFRONT_CART_ATTRIBUTES } from "@tablescape/constants";
import { type useCartLine } from "@shopify/hydrogen-react";
// eslint-disable-next-line no-restricted-imports
import { formatProduct } from "@tablescape/utilities/product";

export const formatCartCollection = (collection: {
  products: {
    nodes: CartProductConnection[];
  };
}) => ({
  products: collection.products.nodes.map((node) => formatProduct(node)),
});

export const hasCartLineAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key: STOREFRONT_CART_ATTRIBUTES,
  valueFn?: (value: Maybe<string> | undefined) => boolean
) =>
  !!cartLine?.attributes?.some((attr) => {
    return attr?.key === key && (valueFn ? valueFn(attr?.value) : true);
  });

export const getCartLineAttributeValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key: STOREFRONT_CART_ATTRIBUTES
) =>
  cartLine?.attributes?.find((attr) => {
    return attr?.key === key;
  })?.value;

export const findRelatedCartProductGiftWithPurchaseLine = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  id: CartLine["id"]
) =>
  lines?.find(
    (giftLine) =>
      giftLine?.id === id &&
      hasCartLineAttribute(
        giftLine,
        STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
      )
  );

export const findRelatedCartSpendingGiftWithPurchaseLine = (
  lines:
    | PartialDeep<
        (CartLine | ComponentizableCartLine)[],
        { recurseIntoArrays: true }
      >
    | undefined,
  spendingThreshold?: string
) => {
  return lines?.find((line) =>
    hasCartLineAttribute(
      line,
      STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE,
      (value) => {
        if (value) {
          try {
            const parsedValue = JSON.parse(value);
            return spendingThreshold
              ? parsedValue?.cartSpendingGiftThreshold === spendingThreshold
              : parsedValue?.cartSpendingGiftThreshold;
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    )
  );
};

export const getGiftWithPurchaseAttributeValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined,
  key?: keyof CartGiftWithPurchaseAttributeValue
) => {
  const gwpAttribute = cartLine?.attributes?.find(
    (attribute) =>
      attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
  );
  try {
    const parsedValue = gwpAttribute?.value
      ? JSON.parse(gwpAttribute.value)
      : undefined;
    return key && parsedValue ? parsedValue[key] : parsedValue;
  } catch (e) {
    return undefined;
  }
};

export const getRelatedCartLinesToRemove = (
  cartLineId: CartLine["id"],
  selectedGiftsWithPurchase:
    | ReturnType<typeof useCartLine>["selectedGiftsWithPurchase"]
    | undefined
) => {
  let linesToRemove = [cartLineId];

  if (selectedGiftsWithPurchase?.length) {
    linesToRemove = linesToRemove.concat(
      selectedGiftsWithPurchase
        .filter((giftLine): giftLine is CartLine => !!giftLine?.id)
        .map(({ id }) => id)
    );
  }

  return linesToRemove;
};

export const addIntelligemsLineItemAttribute = (
  variantId: string,
  attributes: Maybe<AttributeInput[]>
) => {
  if (!("igData" in window) || !attributes) {
    return attributes;
  }

  const igPrice = window.igData?.price.getPriceByVariantId(variantId);
  const currencyRate = window.Shopify?.currency?.rate;

  if (!igPrice || !currencyRate) {
    return attributes;
  }

  const intelligemsLineItemAttribute = {
    key: STOREFRONT_CART_ATTRIBUTES.INTELLIGEMS_PRICE,
    value: String((parseInt(igPrice) / parseFloat(currencyRate)) * 100),
  };

  return [...attributes, intelligemsLineItemAttribute];
};

export const hasIntelligemsLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.INTELLIGEMS_PRICE);

export const hasBundlesLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) => hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.BUNDLES);

export const hasTotalWithoutBMSMLineItemAttribute = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  hasCartLineAttribute(cartLine, STOREFRONT_CART_ATTRIBUTES.TOTAL_WITHOUT_BMSM);

export const getTotalWithoutBMSMValue = (
  cartLine:
    | PartialDeep<
        CartLine | ComponentizableCartLine,
        { recurseIntoArrays: true }
      >
    | undefined
) =>
  parseFloat(
    cartLine?.attributes?.find((attr) => {
      return attr?.key === STOREFRONT_CART_ATTRIBUTES.TOTAL_WITHOUT_BMSM;
    })?.value ?? "0"
  ) / 100;
